/**
 * 内容渲染组件 - 用于展示富文本内容
 * 使用ReactPlayer替代原生video标签，提供更好的播放体验
 */

import React, { useMemo } from 'react';
import '../CustomRichTextEditor/CustomRichTextEditor.css';
import './ContentRenderer.css';
import VideoPlayer from '../VideoPlayer';
import { processAttachmentUrls } from '../../../utils/contentUtils';
import { getApiBaseUrl } from '../../../config/apiConfig';

interface ContentRendererProps {
  content: string;
  className?: string;
  style?: React.CSSProperties;
}

interface VideoInfo {
  id: string;
  url: string;
  placeholder: string;
  containerStyle?: React.CSSProperties;
}

const ContentRenderer: React.FC<ContentRendererProps> = ({ content, className = '', style }) => {
  const baseUrl = getApiBaseUrl();
  const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;

  // 解析内容，提取视频信息并用占位符替换
  const { processedHtml, videos } = useMemo(() => {
    const processed = processAttachmentUrls(content || '');
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = processed;
    
    // 处理图片：确保Base64、相对路径和完整URL都能正确显示
    const images = tempDiv.querySelectorAll('img');
    images.forEach((img) => {
      const currentSrc = img.getAttribute('src') || '';
      
      // Base64图片（data:image/...）保持原样
      if (currentSrc.startsWith('data:')) {
        return;
      }
      
      // blob URL保持原样
      if (currentSrc.startsWith('blob:')) {
        return;
      }
      
      // 相对路径（/attachments/...）转换为完整URL
      if (currentSrc.startsWith('/attachments/')) {
        const cleanPath = currentSrc.startsWith('/') ? currentSrc : `/${currentSrc}`;
        img.setAttribute('src', `${cleanBaseUrl}${cleanPath}`);
        return;
      }
      
      // 如果已经是完整URL（http://或https://），保持不变
      if (currentSrc.startsWith('http://') || currentSrc.startsWith('https://')) {
        return;
      }
      
      // 其他情况：可能是相对路径但没有/attachments/前缀，尝试添加baseUrl
      if (currentSrc && !currentSrc.startsWith('/')) {
        img.setAttribute('src', `${cleanBaseUrl}/${currentSrc}`);
      }
    });
    
    const videoList: VideoInfo[] = [];
    let videoIndex = 0;
    
    // 查找所有视频容器
    const videoContainers = tempDiv.querySelectorAll('.video-container, .editable-video');
    videoContainers.forEach((container) => {
      const video = container.querySelector('video');
      if (!video) return;
      
      // 获取视频URL（优先从data-src获取，如果没有则从source或video的src获取）
      let videoSrc = '';
      if (container instanceof HTMLElement) {
        const dataSrc = container.getAttribute('data-src');
        if (dataSrc) {
          videoSrc = dataSrc;
        }
      }
      
      if (!videoSrc) {
        const source = video.querySelector('source');
        videoSrc = source?.getAttribute('src') || video.getAttribute('src') || '';
      }
      
      if (!videoSrc) return;
      
      // 构建完整URL
      // 如果是blob URL或data URL或已经是完整URL，直接使用
      let finalUrl = videoSrc;
      if (!videoSrc.startsWith('http') && !videoSrc.startsWith('blob:') && !videoSrc.startsWith('data:')) {
        // 只有相对路径才需要添加baseUrl
        const relativePath = videoSrc.startsWith('/') ? videoSrc : `/${videoSrc}`;
        finalUrl = `${cleanBaseUrl}${relativePath}`;
      }
      
      // 提取容器的样式（特别是width和height）
      const containerStyle: React.CSSProperties = {};
      if (container instanceof HTMLElement) {
        const style = container.style;
        const htmlStyleAttr = container.getAttribute('style') || '';
        
        // 解析style属性字符串，提取所有样式
        if (htmlStyleAttr) {
          const stylePairs = htmlStyleAttr.split(';').filter(s => s.trim());
          stylePairs.forEach(pair => {
            const [prop, value] = pair.split(':').map(s => s.trim());
            if (prop && value) {
              // 转换为React CSS属性名（camelCase）
              const camelProp = prop.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
              if (prop === 'width' || prop === 'height' || prop === 'max-width' || prop === 'min-width' || prop === 'min-height') {
                (containerStyle as any)[camelProp] = value;
              }
            }
          });
        }
        
        // 如果从HTML解析到的样式不完整，补充从style对象读取的样式
        if (!containerStyle.width && style.width) {
          containerStyle.width = style.width;
        }
        if (!containerStyle.height && style.height) {
          containerStyle.height = style.height;
        }
        if (!containerStyle.maxWidth && style.maxWidth) {
          containerStyle.maxWidth = style.maxWidth;
        }
        if (!containerStyle.minWidth && style.minWidth) {
          containerStyle.minWidth = style.minWidth;
        }
        if (!containerStyle.minHeight && style.minHeight) {
          containerStyle.minHeight = style.minHeight;
        }
      }
      
      const videoId = `video-${videoIndex++}`;
      const placeholder = `__VIDEO_PLACEHOLDER_${videoId}__`;
      
      // 用占位符替换整个容器
      const placeholderElement = document.createTextNode(placeholder);
      container.parentNode?.replaceChild(placeholderElement, container);
      
      videoList.push({ 
        id: videoId, 
        url: finalUrl, 
        placeholder,
        containerStyle: Object.keys(containerStyle).length > 0 ? containerStyle : undefined
      });
    });
    
    // 也查找独立的video标签（不在video-container中）
    const standaloneVideos = tempDiv.querySelectorAll('video:not(.video-container video):not(.editable-video video)');
    standaloneVideos.forEach((video) => {
      const source = video.querySelector('source');
      const videoSrc = source?.getAttribute('src') || video.getAttribute('src') || '';
      if (!videoSrc) return;
      
      // 构建完整URL（如果是blob URL或data URL或已经是完整URL，直接使用）
      let finalUrl = videoSrc;
      if (!videoSrc.startsWith('http') && !videoSrc.startsWith('blob:') && !videoSrc.startsWith('data:')) {
        // 只有相对路径才需要添加baseUrl
        const relativePath = videoSrc.startsWith('/') ? videoSrc : `/${videoSrc}`;
        finalUrl = `${cleanBaseUrl}${relativePath}`;
      }
      
      const videoId = `video-${videoIndex++}`;
      const placeholder = `__VIDEO_PLACEHOLDER_${videoId}__`;
      
      const placeholderElement = document.createTextNode(placeholder);
      video.parentNode?.replaceChild(placeholderElement, video);
      
      videoList.push({ id: videoId, url: finalUrl, placeholder });
    });
    
    return {
      processedHtml: tempDiv.innerHTML,
      videos: videoList
    };
  }, [content, cleanBaseUrl]);

  // 替换占位符为React组件
  const renderContent = () => {
    if (!videos.length) {
      return <div dangerouslySetInnerHTML={{ __html: processedHtml }} />;
    }

    // 按占位符分割内容
    const parts: Array<string | VideoInfo> = [];
    let remainingHtml = processedHtml;
    let lastIndex = 0;

    videos.forEach((video) => {
      const index = remainingHtml.indexOf(video.placeholder);
      if (index === -1) return;

      // 添加占位符之前的内容
      if (index > lastIndex) {
        parts.push(remainingHtml.substring(lastIndex, index));
      }

      // 添加视频信息
      parts.push(video);

      // 更新剩余HTML和索引
      remainingHtml = remainingHtml.substring(index + video.placeholder.length);
      lastIndex = 0;
    });

    // 添加最后剩余的内容
    if (remainingHtml.length > 0) {
      parts.push(remainingHtml);
    }

    return (
      <>
        {parts.map((part, index) => {
          if (typeof part === 'string') {
            return <div key={`html-${index}`} dangerouslySetInnerHTML={{ __html: part }} />;
          } else {
            // 应用容器的样式到VideoPlayer包装器
            const wrapperStyle: React.CSSProperties = {
              display: 'inline-block',
              margin: '16px 0',
              maxWidth: '100%',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)',
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
              position: 'relative',
              ...part.containerStyle, // 应用从容器提取的样式（width、height等）
            };
            
            return (
              <div 
                key={part.id} 
                className="video-container-wrapper" 
                style={wrapperStyle}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  return false;
                }}
              >
                <VideoPlayer url={part.url} />
              </div>
            );
          }
        })}
      </>
    );
  };

  return (
    <div 
      className={`content-renderer-preview ${className}`}
      style={{
        maxWidth: '100%',
        wordWrap: 'break-word',
        lineHeight: '1.8',
        fontSize: '16px',
        color: '#333',
        ...style
      }}
    >
      <div 
        className="preview-content"
        style={{
          maxWidth: '100%',
          wordWrap: 'break-word',
          lineHeight: '1.8',
          textIndent: 0
        }}
      >
        {renderContent()}
      </div>
    </div>
  );
};

export default ContentRenderer;

